import { useQuery } from '@tanstack/react-query';
import { useState, type ComponentProps, type JSX } from 'react';

import { useConnectorPresence } from '@/integration/hooks/useConnections.js';
import { connectorStatusQueryOptions } from '@/lib/api/quality-setup.js';

import {
  mergeConnectorStatus,
  useConnectorBadgeStatus,
  type QualityConnectorId,
} from './connector-status.js';
import { ConnectorModal } from './ConnectorModal.js';

/**
 * A connector's modal, opened by code rather than by a badge click. Its clicks and keys stop here,
 * because the modal portals out of the DOM but not out of the React tree.
 */
export function ConnectorPrompt({
  provider,
  projectId,
  workspaceId,
  onClose,
  ...modalProps
}: {
  provider: QualityConnectorId;
  projectId: string | undefined;
  workspaceId: string | null;
  onClose: () => void;
} & Pick<
  ComponentProps<typeof ConnectorModal>,
  'statusOverride' | 'onChoiceChange' | 'explainUnavailableActions'
>): JSX.Element {
  const status = useConnectorBadgeStatus(provider, projectId, workspaceId);
  return (
    <span
      className="contents"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <ConnectorModal
        {...modalProps}
        provider={provider}
        status={status}
        projectId={projectId}
        workspaceId={workspaceId}
        open
        onOpenChange={(next) => {
          if (!next) onClose();
        }}
      />
    </span>
  );
}

/**
 * Stops an action that needs a connector the user has neither connected nor chosen an owner for,
 * and opens that connector's modal instead. `provider` is null when the action needs no connector.
 * While a status read is loading, failed or unavailable the action runs, and the server's own check
 * stays the guard. An expired connection blocks only when no healthy one sits beside it, since
 * Quality may use the healthy one. `blocked` lets a caller keep its action button clickable so the
 * click can open the modal. `onClose` runs when the prompt closes, so the caller can re-read what
 * the connection changed.
 */
export function useConnectorGate(
  provider: QualityConnectorId | null,
  projectId: string | undefined,
  workspaceId: string | null,
  onClose?: () => void,
): {
  blocked: boolean;
  stop: () => boolean;
  prompt: JSX.Element | null;
} {
  const [prompting, setPrompting] = useState(false);
  const qualityStatus = useQuery(connectorStatusQueryOptions(projectId));
  const presence = useConnectorPresence(workspaceId);
  const readsSucceeded =
    qualityStatus.isSuccess &&
    presence.isSuccess &&
    qualityStatus.data?.unavailable !== true &&
    presence.data?.readFailed !== true;
  const state =
    provider !== null && readsSucceeded
      ? mergeConnectorStatus(provider, qualityStatus.data, presence.data).state
      : 'connected';
  const hasHealthyConnection =
    provider !== null &&
    (presence.data?.providers[provider]?.connections ?? []).some(
      (connection) => connection.needsReconnect !== true,
    );
  const blocked =
    state === 'disconnected' || (state === 'error' && !hasHealthyConnection);
  return {
    blocked,
    stop: () => {
      if (blocked) setPrompting(true);
      return blocked;
    },
    prompt:
      prompting && provider ? (
        <ConnectorPrompt
          provider={provider}
          projectId={projectId}
          workspaceId={workspaceId}
          onClose={() => {
            setPrompting(false);
            onClose?.();
          }}
        />
      ) : null,
  };
}
